import React, { useState } from "react";
import { Box, Button, Chip, Collapse, IconButton, LinearProgress, Paper, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DataTable, formatDate, relTime } from "../../Layout/Common";
import { FIXED_TABLE, isActive, usd4 } from "./loc";

const STATUS_COLOR = { queued: "default", running: "info", done: "success", failed: "error", canceled: "warning" };

function scopeOf(job) {
    const s = job.scope ?? {};
    const parts = [];
    if (s.folder) parts.push(s.folder);
    else if (s.paths?.length) parts.push(s.paths.length === 1 ? s.paths[0] : `${s.paths.length} tags`);
    else parts.push("everything");
    if (s.languages?.length && job.kind !== "review") parts.push(s.languages.length === 1 ? s.languages[0] : `${s.languages.length} languages`);
    if (s.all) parts.push("all tiers"); else if (s.force) parts.push("force");
    return parts.join(" · ");
}

// The title's translation jobs (the Jobs tab): the active ones with progress and a cancel, the finished ones as a
// table. `standalone` = the tab (always open, every recent job); without it, a collapsible panel.
export default function JobPanel({ jobs, canEdit, onCancel, standalone }) {
    const [open, setOpen] = useState(true);
    const list = jobs?.jobs ?? [];
    const active = list.filter(isActive);
    const finished = list.filter(j => !isActive(j)).slice(0, standalone ? 30 : 8);
    if (list.length === 0) return standalone ? <Typography color="text.secondary">No translation jobs yet. A created tag, "Translate now", the Coverage actions and a changed news item (a context job over its folder) start one; they run inline when small and on the scheduler otherwise.</Typography> : null;
    const shown = standalone || open;
    const columns = [
        { id: "kind", label: "Job", width: "9%", render: j => <Chip size="small" label={j.kind} /> },
        { id: "scope", label: "Scope", width: "22%", render: scopeOf },
        { id: "status", label: "Status", width: "9%", render: j => <Chip size="small" color={STATUS_COLOR[j.status] ?? "default"} label={j.status} /> },
        { id: "summary", label: "Result", width: "39%", render: j => <Typography variant="body2">{j.summary || (j.errors?.[0] ?? "")}</Typography> },
        { id: "usd", label: "Cost", align: "right", width: "9%", render: j => usd4(j.usd) },
        { id: "when", label: "When", width: "12%", render: j => <Tooltip title={`${j.createdBy} · ${formatDate(j.createdDate)}`}><span>{relTime(j.finishedAt ?? j.createdDate)}</span></Tooltip> },
    ];
    return (
        <Paper sx={{ p: 1.5, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {!standalone && <IconButton size="small" onClick={() => setOpen(!open)}>{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>}
                <Typography variant="subtitle2">Translation jobs</Typography>
                {active.length > 0 && <Chip size="small" color="info" label={`${active.length} running`} />}
                <Typography variant="caption" color="text.secondary">{finished.length ? `${finished.length} recent` : ""}{standalone ? " · refreshed every 5 s while a job runs" : ""}</Typography>
            </Box>
            <Collapse in={shown}>
                {active.map(j => {
                    const total = j.unitsTotal || 1, done = j.unitsDone ?? 0;
                    return (
                        <Box key={j.id} sx={{ mt: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Chip size="small" color="info" label={`${j.kind} · ${j.status}`} />
                                <Typography variant="body2">{scopeOf(j)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {done}/{total} units · {j.cells?.written ?? 0} written, {j.cells?.kept ?? 0} kept, {j.cells?.skipped ?? 0} skipped, {j.cells?.failed ?? 0} failed · {usd4(j.usd)}
                                    {j.estimate?.usd ? ` (estimated ${usd4(j.estimate.usd)})` : ""}{j.stale ? " · no heartbeat, the runner resumes on the next tick" : ""}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                {canEdit && !j.cancelRequested && <Button size="small" color="warning" onClick={() => onCancel(j.id)}>Cancel</Button>}
                                {j.cancelRequested && <Chip size="small" color="warning" label="cancelling" />}
                            </Box>
                            <LinearProgress variant="determinate" value={Math.min(100, done / total * 100)} sx={{ mt: 1 }} />
                            {j.warnings?.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{j.warnings[j.warnings.length - 1]}</Typography>}
                        </Box>
                    );
                })}
                {finished.length > 0 && <Box sx={{ mt: 1 }}><DataTable columns={columns} rows={finished} getRowKey={j => j.id} sx={FIXED_TABLE} /></Box>}
            </Collapse>
        </Paper>
    );
}
